import React, { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';

interface DateOfIncorporationInputProps {
  // Controlled pattern (used by JourneyPage)
  value?: { date_of_incorporation?: string | null };
  onChange?: (val: { date_of_incorporation: string }) => void;
  disabled?: boolean;
  // Legacy pattern
  initialData?: { date_of_incorporation?: string };
  onDataChange?: (data: { date_of_incorporation: string }) => void;
}

const DateOfIncorporationInput: React.FC<DateOfIncorporationInputProps> = ({
  value,
  onChange,
  disabled,
  initialData,
  onDataChange,
}) => {
  const dayRef = React.useRef<HTMLInputElement>(null);
  const monthRef = React.useRef<HTMLInputElement>(null);
  const yearRef = React.useRef<HTMLInputElement>(null);
  const dateInputRef = React.useRef<HTMLInputElement>(null);
  
  const [day, setDay] = React.useState('');
  const [month, setMonth] = React.useState('');
  const [year, setYear] = React.useState('');

  // Backend may return ISO datetime string or DD-MM-YYYY
  const rawDoi = value?.date_of_incorporation ?? initialData?.date_of_incorporation ?? '';

  // Parse date_of_incorporation into day, month, year
  React.useEffect(() => {
    if (rawDoi) {
      let dateStr = rawDoi;
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      }
      const parts = dateStr.split('-');
      if (parts.length === 3) {
        if (parts[0].length <= 2) {
          // DD-MM-YYYY format
          setDay(parts[0]);
          setMonth(parts[1]);
          setYear(parts[2]);
        } else {
          // YYYY-MM-DD format
          setDay(parts[2]);
          setMonth(parts[1]);
          setYear(parts[0]);
        }
      }
    } else {
      setDay('');
      setMonth('');
      setYear('');
    }
  }, [rawDoi]);

  const updateParent = (d: string, m: string, y: string) => {
    if (d.length === 2 && m.length === 2 && y.length === 4) {
      const ddmmyyyy = `${d}-${m}-${y}`;
      onChange?.({ date_of_incorporation: ddmmyyyy });
      onDataChange?.({ date_of_incorporation: ddmmyyyy });
    }
  };

  const handleDayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setDay(value);
    if (value.length === 2) {
      setTimeout(() => {
        monthRef.current?.focus();
        monthRef.current?.select();
      }, 0);
    }
    updateParent(value, month, year);
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 2);
    setMonth(value);
    if (value.length === 2) {
      setTimeout(() => {
        yearRef.current?.focus();
        yearRef.current?.select();
      }, 0);
    }
    updateParent(day, value, year);
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 4);
    setYear(value);
    updateParent(day, month, value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'day' | 'month' | 'year') => {
    if (e.key === 'Backspace') {
      if (field === 'month' && month === '' && day) {
        dayRef.current?.focus();
      } else if (field === 'year' && year === '' && month) {
        monthRef.current?.focus();
      }
    }
  };

  const toISOFormat = () => {
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      return `${year}-${month}-${day}`;
    }
    return '';
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isoValue = e.target.value; // YYYY-MM-DD
    if (isoValue) {
      const [y, m, d] = isoValue.split('-');
      setDay(d);
      setMonth(m);
      setYear(y);
      const ddmmyyyy = `${d}-${m}-${y}`;
      onChange?.({ date_of_incorporation: ddmmyyyy });
      onDataChange?.({ date_of_incorporation: ddmmyyyy });
    }
  };

  const handleCalendarClick = () => {
    dateInputRef.current?.showPicker?.();
  };

  const doi = toISOFormat();

  // Calculate detailed business age from incorporation date (using ISO format)
  const businessAge = useMemo(() => {
    if (!doi) return null;

    const incorporationDate = new Date(doi);
    const today = new Date();

    // Calculate total difference in milliseconds
    const diffMs = today.getTime() - incorporationDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    // Calculate years, months, weeks, days
    let years = today.getFullYear() - incorporationDate.getFullYear();
    let months = today.getMonth() - incorporationDate.getMonth();
    let days = today.getDate() - incorporationDate.getDate();

    if (days < 0) {
      months--;
      const prevMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      days += prevMonth.getDate();
    }

    if (months < 0) {
      years--;
      months += 12;
    }

    const weeks = Math.floor(days / 7);
    const remainingDays = days % 7;

    return { years, months, weeks, days: remainingDays, totalDays: diffDays };
  }, [doi]);

  // Format age display based on duration
  const formatAge = (age: { years: number; months: number; weeks: number; days: number; totalDays: number } | null) => {
    if (!age) return null;

    const parts: string[] = [];

    // Less than 1 week - show days
    if (age.totalDays < 7) {
      return `${age.totalDays} ${age.totalDays === 1 ? 'day' : 'days'}`;
    }

    // Less than 1 month - show weeks and days
    if (age.years === 0 && age.months === 0) {
      if (age.weeks > 0) parts.push(`${age.weeks} ${age.weeks === 1 ? 'week' : 'weeks'}`);
      if (age.days > 0) parts.push(`${age.days} ${age.days === 1 ? 'day' : 'days'}`);
      return parts.join(' and ');
    }

    // Less than 1 year - show months and weeks
    if (age.years === 0) {
      if (age.months > 0) parts.push(`${age.months} ${age.months === 1 ? 'month' : 'months'}`);
      if (age.weeks > 0) parts.push(`${age.weeks} ${age.weeks === 1 ? 'week' : 'weeks'}`);
      return parts.join(' and ');
    }

    // 1 year or more - show years and months
    if (age.years > 0) parts.push(`${age.years} ${age.years === 1 ? 'year' : 'years'}`);
    if (age.months > 0) parts.push(`${age.months} ${age.months === 1 ? 'month' : 'months'}`);

    return parts.join(' and ');
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="date_of_incorporation" className="text-gray-500 font-medium">
        Date of Incorporation <span className="text-red-500">*</span>
      </Label>
      <div className="relative flex items-center border border-gray-200 focus-within:border-intellend-green rounded-xl py-3 px-4 bg-white" style={{ gap: '2px' }}>
        <input
          ref={dayRef}
          type="text"
          inputMode="numeric"
          value={day}
          onChange={handleDayChange}
          onKeyDown={(e) => handleKeyDown(e, 'day')}
          placeholder="DD"
          maxLength={2}
          disabled={disabled}
          style={{ width: '2rem', padding: 0, margin: 0, textAlign: 'center', background: 'transparent' }}
          className="border-0 focus:outline-none focus:ring-0"
        />
        <span className="text-gray-600">-</span>
        <input
          ref={monthRef}
          type="text"
          inputMode="numeric"
          value={month}
          onChange={handleMonthChange}
          onKeyDown={(e) => handleKeyDown(e, 'month')}
          placeholder="MM"
          maxLength={2}
          disabled={disabled}
          style={{ width: '2.25rem', padding: 0, margin: 0, textAlign: 'center', background: 'transparent' }}
          className="border-0 focus:outline-none focus:ring-0"
        />
        <span className="text-gray-600">-</span>
        <input
          ref={yearRef}
          type="text"
          inputMode="numeric"
          value={year}
          onChange={handleYearChange}
          onKeyDown={(e) => handleKeyDown(e, 'year')}
          placeholder="YYYY"
          maxLength={4}
          disabled={disabled}
          style={{ width: '3.5rem', padding: 0, margin: 0, textAlign: 'center', background: 'transparent' }}
          className="border-0 focus:outline-none focus:ring-0"
        />
        <input
          ref={dateInputRef}
          id="date_of_incorporation"
          type="date"
          value={doi}
          onChange={handleDatePickerChange}
          disabled={disabled}
          max={new Date().toISOString().split('T')[0]}
          min="1900-01-01"
          className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
          style={{ zIndex: 10 }}
        />
        <svg
          className="ml-auto text-gray-400 pointer-events-none"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      </div>
      {businessAge && (
        <p className="text-sm text-gray-600 mt-2 px-1">
          Business age: {formatAge(businessAge)}
        </p>
      )}
    </div>
  );
};

export default DateOfIncorporationInput;
